<template>
  <!--Buttons for existing transactions-->
  <el-col :span="12" class="h-padded right-aligned-col" v-if="status">
    <span v-if="!isBalanced" class="unbalanced-tag">
      Unbalanced
    </span>
    <span
      v-else-if="['POSTED', 'CLOSED'].includes(status) && amountHasChanged && transactionType.name === 'je'"
      class="unbalanced-tag">
      Debits/Credits must equal original Posted amount when changing Distribution Lines
    </span>
    <el-button size="small"
      v-if="showProscanToggle"
      class="proscan-file-toggle"
      :type="proscanFileVisible ? 'primary' : 'default'"
      :title="proscanFileVisible ? 'Hide the document pane' : 'Show the document pane'"
      icon="document"
      @click="$emit('toggleProscanFile')" />
    <el-button
      size="small"
      stye="font-size: 13px;"
      :disabled="!((allowDraftFileUpload && status === 'DRAFT') || (isBalanced && saveOnlyStatuses && saveOnlyStatuses.includes(status))) || !hasPermission.create"
      @click="() => otherActions('uploadDocument')">
      Files {{ numberOfFiles ? `(${numberOfFiles})` : '' }}
    </el-button>
    <el-button type="warning" plain @click="$emit('convertToAP', false)" v-if="transactionType.name === 'po' && status === 'POSTED'" :disabled="converted_to_ap || !hasPermission.createAP">
      Convert To AP
    </el-button>
    <el-dropdown
      style="padding-top: 4px;"
      v-if="transactionType.name === 'po' && ['DRAFT', 'OPEN', 'POSTED', 'CLOSED', 'VOIDED'].includes(status)"
      split-button
      size="small"
      type="default"
      :disabled="isSaving"
      @command='otherActions'>
      Other Actions
      <template v-slot:dropdown><el-dropdown-menu>
        <el-dropdown-item
          command="printpo">
          Print PO
        </el-dropdown-item>
        <el-dropdown-item
           command="emailpo">
           Email PO
         </el-dropdown-item>
      </el-dropdown-menu></template>
    </el-dropdown>
    <el-button size="small" type="warning" plain @click="$emit('goBack', false)" :disabled="isSaving" data-testid="back-btn">
      Back
    </el-button>
    <el-dropdown
      style="padding-top: 4px;"
      v-if="transactionType.name !== 'po'"
      split-button
      type="default"
      size="small"
      :disabled="isSaving"
      @command='otherActions'>
      Other Actions
      <template v-slot:dropdown><el-dropdown-menu>
        <el-dropdown-item
          :disabled="!(isBalanced) || !hasPermission.create"
          command="duplicateTransaction">
          Duplicate Transaction
        </el-dropdown-item>
        <el-dropdown-item
          :disabled="disableDistChanges || !(hasPermission.create && hasPermission.edit)"
          command="toggleDistChanges">
          {{ isReadOnly ? 'Enable' : 'Disable' }} Distribution Changes
        </el-dropdown-item>
        <el-dropdown-item
          v-if="transactionType.name === 'pc' && status === 'POSTED' && canUnpost"
          :disabled="isRecon || hasDcs || !hasPermission.post"
          command="unPostTransaction">
          Unpost Transaction
        </el-dropdown-item>
        <el-dropdown-item
          v-if="transactionType.name === 'pc'"
          :disabled="!(isBalanced && parentForm.amount_remaining_pc_decimal < 0 && saveOnlyStatuses.includes(status)) || !hasPermission.create"
          command="reimbursePcVendor">
          Create A/P Reimbursement Invoice
        </el-dropdown-item>
        <el-dropdown-item
          v-if="status === 'POSTED' && !isPaid "
          :disabled="isRecon || !hasPermission.post"
          command="void">
          Void
        </el-dropdown-item>
        <el-dropdown-item
          v-if="allowDeleteStatuses.includes(status)"
          :disabled="isRecon || !hasPermission.delete"
          command="delete">
          Delete
        </el-dropdown-item>
        <el-dropdown-item
          v-if="['POSTED', 'CLOSED'].includes(status) && !hasBeenChanged && hasPermission.read"
          :disabled="!isBalanced || disableAuditBtn"
          :loading="disableAuditBtn"
          command="postReportBtnClick">
          Posting Report
        </el-dropdown-item>
      </el-dropdown-menu></template>
    </el-dropdown>
    <el-button
      v-if="status !== 'DRAFT'"
      @click="$emit('addNew')"
      :disabled="isSaving || !hasPermission.create"
      icon="plus"
      data-testid="add-new-btn"
    />
    <el-button
      v-if="status === 'DRAFT' && !isBalanced"
      :disabled="!hasBeenChanged || !isSaveable || !hasPermission.create"
      type="primary"
      :loading="isSaving"
      @click="handleDropdownClick('1')"
      data-testid="save-primary-action">
      Save Draft
    </el-button>
    <el-button
      v-else-if="status === 'DRAFT' && isBalanced && hasBeenChanged"
      :disabled="!isSaveable || !hasPermission.create"
      type="primary"
      :loading="isSaving"
      @click="handleDropdownClick(transactionType.name === 'po' ? '2' : '1')"
      data-testid="save-primary-action">
      Save
    </el-button>
    <el-button
      v-else-if="status === 'DRAFT' && isBalanced"
      :disabled="!isSaveable || !hasPermission.post"
      type="primary"
      :loading="isSaving"
      @click="handleDropdownClick('1')"
      data-testid="save-primary-action">
      Open
    </el-button>
    <el-dropdown
      split-button
      type="primary"
      v-if="hasBeenChanged && !dropdownStatuses.includes(status) && !isSaving && isBalanced && isSaveable"
      @command='handleDropdownClick'
      @click="hasBeenChanged && status === 'AUDITED' ? handleDropdownClick('2') : handleDropdownClick('1')"
      trigger="click"
      data-testid="save-primary-action">
      Save
      <template v-slot:dropdown><el-dropdown-menu>
        <el-dropdown-item v-if="saveAndAuditStatuses.includes(status) && hasPermission.create" command="3">
          Save + Audit
        </el-dropdown-item>
        <el-dropdown-item v-if="saveAndPostStatuses.includes(status) && hasPermission.post" command="4">
          Save + Post
        </el-dropdown-item>
      </el-dropdown-menu></template>
    </el-dropdown>
    <el-button
      v-else-if="hasBeenChanged && !dropdownStatuses.includes(status) && isBalanced && isSaving"
      type="primary"
      :loading="isSaving"
      data-testid="save-primary-action">
      Saving
    </el-button>
    <el-button
      v-else-if="saveOnlyStatuses.includes(status)"
      :disabled="!(hasBeenChanged && isBalanced && isSaveable) || isSaving || !hasPermission.create"
      :loading="isSaving"
      type="primary"
      @click="handleDropdownClick('1')"
      data-testid="save-primary-action">
      Save
    </el-button>
    <el-button
      v-if="['OPEN', 'AUDITED'].includes(status) && !hasBeenChanged && hasPermission.read"
      type="success"
      :disabled="!isBalanced || disableAuditBtn"
      :loading="disableAuditBtn"
      @click="$emit('auditBtnClick')"
      data-testid="audit-btn">
      Audit
    </el-button>
    <el-button
      v-if="postBtnStatuses.includes(status) && !hasBeenChanged && hasPermission.post"
      type="success"
      :disabled="!isBalanced || disableAuditBtn"
      :loading="disablePostBtn"
      @click="$emit('postBtnClick')"
      data-testid="post-btn">
      Post
    </el-button>
    <el-button
      v-if="transactionType.name === 'ar' && status  === 'POSTED'"
      type="primary"
      :loading="printingOnly"
      @click="$emit('postBtnClick')">
      {{ printingOnly ? 'Printing' : 'Print Copy' }}
    </el-button>
  </el-col>
  <!--Buttons for new transactions-->
  <el-col :span="isSaveable && isSaving ? 6 : 12" class="h-padded right-aligned-col" v-else>
    <span v-if="!isBalanced" class="unbalanced-tag">
      Unbalanced
    </span>
    <el-button size="small" type="warning" plain @click="$emit('goBack', false)" :disabled="isSaving" data-testid="back-btn">
      Back
    </el-button>
    <el-button
      v-if="!isOpenable"
      type="primary"
      :disabled="!isSaveable || !hasPermission.create"
      :loading="isSaving"
      @click="$emit('submit', false)"
      data-testid="save-primary-action">
      Save as Draft
    </el-button>
    <el-dropdown
      v-else-if="isSaveable && isOpenable && !isSaving"
      id="save-action-dropdown"
      type="primary"
      split-button
      @command='handleDropdownClick'
      @click="defaultSaveClick()"
      trigger="click"
      data-testid="save-primary-action">
      Save
      <template v-slot:dropdown><el-dropdown-menu>
        <el-dropdown-item command="2">Save as Draft</el-dropdown-item>
        <el-dropdown-item command="3">Save & Add New</el-dropdown-item>
      </el-dropdown-menu></template>
    </el-dropdown>
    <el-button
      v-else-if="isSaveable && isOpenable && isSaving"
      type="primary"
      :loading="isSaving"
      data-testid="save-primary-action">
      Saving
    </el-button>
  </el-col>
</template>

<script>
  export default {
    props: {
      status: String,
      converted_to_ap: Boolean,
      showProscanToggle: Boolean,
      proscanFileVisible: Boolean,
      isSaving: Boolean,
      isSaveable: Boolean,
      isOpenable: Boolean,
      isReadOnly: Boolean,
      isBalanced: Boolean,
      hasBeenChanged: Boolean,
      amountHasChanged: Boolean,
      transactionType: Object,
      parentForm: Object,
      numberOfFiles: Number,
      isPaid: Boolean,
      allowDraftFileUpload: {
        default: false,
        type: Boolean
      },
      isRecon: {
        default: false,
        type: Boolean
      },
      hasPermission: Object,
      dropdownStatuses: {
        default: () => ['DRAFT', 'CLOSED', 'POSTED', 'VOIDED'],
        type: Array
      },
      saveAndAuditStatuses: {
        default: () => ['OPEN', 'AUDITED'],
        type: Array
      },
      saveAndPostStatuses: {
        default: () => ['OPEN', 'AUDITED'],
        type: Array
      },
      saveOnlyStatuses: {
        default: () => ['POSTED', 'OPEN', 'AUDITED', 'CLOSED'],
        type: Array
      },
      allowDeleteStatuses: {
        default: () => ['DRAFT', 'OPEN'],
        type: Array
      },
      disableDistChanges: {
        default: false,
        type: Boolean
      },
      disableAuditBtn: {
        default: false,
        type: Boolean
      },
      disablePostBtn: {
        default: false,
        type: Boolean
      },
      printingOnly: {
        default: false,
        type: Boolean
      },
      postBtnStatuses: {
        default: () => ['OPEN', 'AUDITED'],
        type: Array
      },
      hasDcs: {
        default: false,
        type: Boolean
      }
    },
    methods: {
      defaultSaveClick () {
        let actionNumber = this.transactionType.name === 'po' ? '2' : '1'
        if (this.addAnotherByDefault) {
          actionNumber = '3'
        }
        this.handleDropdownClick(actionNumber)
      },
      handleDropdownClick (val) {
        this.$emit('dropdownClick', val)
      },
      otherActions (val) {
        this.$emit('otherActions', val)
      }
    },
    computed: {
      addAnotherByDefault () {
        this.$store.dispatch('fetchUiSetting', { name: 'ADD_ANOTHER_TRANSACTION_BY_DEFAULT' })
        return !!this.$store.getters.getUiSettings['ADD_ANOTHER_TRANSACTION_BY_DEFAULT']
      },
      canUnpost () {
        return this.$store.getters.getUiSettings['UNPOST_PC_TRANSACTIONS']
      }
    }
  }
</script>

<style scoped>
  .el-button {
    margin: 0;
  }
  /* Element spaces adjacent buttons by 10px, which reads wide against the small size */
  .el-button + .el-button,
  .el-button + .el-dropdown,
  .el-dropdown + .el-button,
  .el-dropdown + .el-dropdown {
    margin-left: 2px !important;
  }
  .right-aligned-col {
    text-align: right;
  }
  .unbalanced-tag {
    margin: 0 8px;
    background-color: rgba(255,73,73,.1);
    border: 1px solid rgba(255,73,73,.2);
    display: inline-block;
    color: #ff4949;
    padding: 0 5px;
    height: 24px;
    line-height: 22px;
    font-size: 12px;
    border-radius: 4px;
    box-sizing: border-box;
  }
  .el-dropdown-menu__item {
    min-width: 8.4rem !important;
  }
  /* Icon sized so the header row keeps every button on one line */
  .proscan-file-toggle {
    min-width: 0 !important;
    padding-left: 10px;
    padding-right: 10px;
  }
</style>
